import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ceoImage from '../assets/team/image.png';

// Decorative SVG URL (same as in WhatWeExpect)
const decorativeSvg = "https://framerusercontent.com/images/vPkLrm9UJiyijCDtcCPWb74bhoI.svg";

const CEOSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    size: Math.random() * 30 + 10,
    top: Math.random() * 100,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: Math.random() * 10 + 5,
    opacity: Math.random() * 0.3 + 0.1,
  }));

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gradient-to-br from-white via-gray-50 to-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      {/* Background orbs and particles (unchanged) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-200/40 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-200/40 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-100/30 rounded-full blur-3xl" />
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full bg-gradient-to-r from-red-400/20 to-rose-400/20 blur-xl animate-float"
            style={{
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              top: `${particle.top}%`,
              left: `${particle.left}%`,
              opacity: particle.opacity,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
            }}
          />
        ))}
      </div>

      {/* ----- DECORATIVE FLOATING SVGs (exactly as in WhatWeExpect) ----- */}
      {/* Left decorative SVG – moves from top to bottom */}
      <div className="absolute left-0 top-10 w-48 md:w-56 opacity-50 pointer-events-none z-0">
        <div className="relative w-full h-full">
          <img
            src={decorativeSvg}
            alt=""
            className="w-full h-auto animate-float-left drop-shadow-lg"
            style={{ transform: 'scaleX(-1)' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.fallback')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback w-full h-full rounded-full bg-gradient-to-br from-red-400/40 to-rose-400/40 blur-md';
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </div>

      {/* Right decorative SVG – moves from bottom to top */}
      <div className="absolute right-0 bottom-10 w-48 md:w-56 opacity-50 pointer-events-none z-0">
        <div className="relative w-full h-full">
          <img
            src={decorativeSvg}
            alt=""
            className="w-full h-auto animate-float-right drop-shadow-lg"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              if (parent && !parent.querySelector('.fallback')) {
                const fallback = document.createElement('div');
                fallback.className = 'fallback w-full h-full rounded-full bg-gradient-to-br from-red-400/40 to-rose-400/40 blur-md';
                parent.appendChild(fallback);
              }
            }}
          />
        </div>
      </div>
      {/* ----------------------------------------------------------------- */}

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 md:mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold">
            <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
              Shaping the Future: A Message from Our CEO
            </span>
          </h2>
        </motion.div>

        {/* Two‑column layout */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left – Image with animated ring */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6 }}
            className="flex justify-center order-2 lg:order-1"
          >
            <div className="relative w-64 h-64 md:w-96 md:h-96 group">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-red-500 via-rose-500 to-red-500 animate-spin-slow opacity-70 group-hover:opacity-100 transition" />
              <div className="absolute inset-1 rounded-full bg-gradient-to-r from-red-500 to-rose-500 animate-pulse opacity-30" />
              <div className="relative bg-white/40 backdrop-blur-sm rounded-full p-2 shadow-xl w-full h-full">
                <div className="rounded-full overflow-hidden bg-white/20 w-full h-full">
                  <img
                    src={ceoImage}
                    alt="CEO"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x400?text=CEO';
                    }}
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right – Message */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="bg-white/60 backdrop-blur-md rounded-2xl p-6 md:p-8 shadow-xl border border-white/20 hover:border-red-300/50 transition-all duration-300">
              <div className="space-y-4 text-gray-700 leading-relaxed text-sm md:text-base">
                <p>
                  As the CEO of UB Creation, I’m thrilled to share our journey with you. What started as a passion for creating engaging games has grown into a thriving studio dedicated to crafting immersive and unforgettable experiences.
                </p>
                <p>
                  At UB Creation, we’re more than just a team  we’re a family of innovators, storytellers, and gamers at heart. Every project we take on is fueled by creativity and a commitment to delivering games that leave a lasting impact. We listen to our players, value feedback, and continuously evolve to meet the ever-changing landscape of the gaming world.
                </p>
                <p>
                  Looking ahead, we’re excited to explore new horizons, create groundbreaking games, and continue building a global community of players.
                </p>
                <p className="italic border-l-4 border-red-400 pl-4">
                  Thank you for being part of our story — the best is yet to come!
                </p>
                <p className="mt-4">
                  Warm regards,<br />
                  Abdul Rehman<br />
                  CEO, UB Creation
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          50% { transform: translateY(-15px) translateX(5px); }
        }
        @keyframes spin-slow {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
        /* Exact animations from WhatWeExpect */
        @keyframes floatLeft {
          0% { transform: translateY(0) scaleX(-1); }
          50% { transform: translateY(40px) scaleX(-1); }
          100% { transform: translateY(0) scaleX(-1); }
        }
        .animate-float-left {
          animation: floatLeft 6s ease-in-out infinite;
        }
        @keyframes floatRight {
          0% { transform: translateY(0); }
          50% { transform: translateY(-40px); }
          100% { transform: translateY(0); }
        }
        .animate-float-right {
          animation: floatRight 6s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CEOSection;